import { Component, Input, Output, EventEmitter, TemplateRef, ViewEncapsulation, Optional, Self, OnInit, OnDestroy, DoCheck } from '@angular/core';
import { ControlValueAccessor, NgControl, FormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonTextfieldData } from './common-textfield.component.model';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Subscription } from 'rxjs';
import { ValidationService } from 'src/app/services/validation.service';

@Component({
  selector: 'wm-common-textfield',
  standalone: false,
  templateUrl: './common-textfield.component.html',
  styleUrl: './common-textfield.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CommonTextfieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
  _value: string | number = '';
  _dateValue: Date | null = null;
  private _valueSubscription: Subscription | undefined;

  private _data: CommonTextfieldData = {
    label: '', placeholder: '', id: '', variant: 'outline',
    type: 'text', required: false, helperText: '',isHelpText: false, instructions: '',
    disabled: false, readonly: false, textAreaRows: 3,
    startIcon: { matIconName: '', onIconClick: () => { } },
    endIcon: { matIconName: '', onIconClick: () => { } },
    value: ''
  };
  disabled = false;

  @Input({ required: true })
  set data(value: CommonTextfieldData) {
    this._data = { ...this._data, ...value };
    this.syncValueFromData();
  }

  get data(): CommonTextfieldData {
    return this._data;
  }

  @Input() dateFilter: (date: Date | null) => boolean = () => true;
  @Input() minDate?: Date | null;
  @Input() maxDate?: Date | null;

  @Output() valueChange = new EventEmitter<any>();

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

 ngOnInit(): void {
    this.setupControlSubscription();

    // Attach the custom validator to the parent control
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.addValidators(this.validate.bind(this));
      this.ngControl.control.updateValueAndValidity({ emitEvent: false });
    }
  }

private syncValueFromData(): void {
  let incomingValue: any = this._data.value ?? '';

  if (this.ngControl?.control && this._data.type === 'date') {
    return;
  }

  if (this._data.type === 'date') {
    if (incomingValue && typeof incomingValue === 'string') {
      const d = new Date(incomingValue);
      if (!isNaN(d.getTime())) {
        incomingValue = d;
      }
    } else if (incomingValue && typeof incomingValue === 'object' && !(incomingValue instanceof Date)) {
      return;
    }
  }

  if (this.ngControl && this.ngControl.control) {
    if (this.ngControl.control.value !== incomingValue) {
      this.ngControl.control.setValue(incomingValue, { emitEvent: false });
      this._value = incomingValue;
    }
  } else {
    if (this._value !== incomingValue) {
      this.writeValue(incomingValue);
    }
  }
}

private setupControlSubscription(): void {
    if (this.ngControl && this.ngControl.control) {
      if ((this.ngControl.control.value === '' || this.ngControl.control.value === null) && this._data.value) {
         this.ngControl.control.setValue(this._data.value, { emitEvent: false });
         this.writeValue(this._data.value);
      }

      this._valueSubscription = this.ngControl.control.valueChanges.subscribe((val) => {
        const newValue = val ?? '';
        if (this._value !== newValue) {
          this.writeValue(newValue); // Safely parse external changes
          this.valueChange.emit(this._value);
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this._valueSubscription) {
      this._valueSubscription.unsubscribe();
    }
  }

  onChange = (_: any) => { };
  onTouched = () => { };

writeValue(value: any): void {
    if (this._data?.type === 'date') {
      if (value && typeof value === 'string' && value !== 'Invalid Date') {
        let d: Date;
        if (value.includes('/') && value.length === 10) {
          const parts = value.split('/');
          d = new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
        } else {
          d = new Date(value);
        }

        if (!isNaN(d.getTime()) && d.getFullYear() > 1) {
          this._dateValue = d;
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          const year = d.getFullYear();
          this._value = `${month}/${day}/${year}`;
        } else {
          this._dateValue = null;
          this._value = '';
        }
      } else if (value instanceof Date) {
        if (!isNaN(value.getTime()) && value.getFullYear() > 1) {
          this._dateValue = value;
          const month = String(value.getMonth() + 1).padStart(2, '0');
          const day = String(value.getDate()).padStart(2, '0');
          const year = value.getFullYear();
          this._value = `${month}/${day}/${year}`;
        } else {
          this._dateValue = null;
          this._value = '';
        }
      } else {
        this._dateValue = null;
        this._value = '';
      }
    } else {
      this._value = value ?? '';
    }
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  private applyPhoneMask(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }


  private shouldApplyEmailValidation(): boolean {
    const configuredFormat = this.getConfiguredFormatValidation();
    if (configuredFormat === 'email') return true;
    if (configuredFormat === 'phone' || configuredFormat === 'none') return false;

    const label = this.data?.label?.toLowerCase() || '';
    return this.data?.type === 'email' || /\bemail\b/.test(label);
  }

  private shouldApplyPhoneMask(): boolean {
    const configuredFormat = this.getConfiguredFormatValidation();
    if (configuredFormat === 'phone') return true;
    if (configuredFormat === 'email' || configuredFormat === 'none') return false;

    const label = this.data?.label?.toLowerCase() || '';
    return this.data?.mask === 'phone' || label.includes('phone') || label.includes('tel');
  }

  private getConfiguredFormatValidation(): 'email' | 'phone' | 'none' | null {
    const raw = this.data?.formatValidation;
    if (raw === null || raw === undefined) return null;

    if (raw === 0) return 'email';
    if (raw === 1) return 'phone';
    if (raw === 3) return 'none';

    const normalized = raw.toString().trim().toLowerCase();
    if (normalized === 'email') return 'email';
    if (normalized === 'phone' || normalized === 'phonenumber') return 'phone';
    if (normalized === 'none') return 'none';

    return null;
  }


 onValueChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    let value = target.value;

    if (this.shouldApplyPhoneMask()) {
      const maskedValue = this.applyPhoneMask(value);
      if (value !== maskedValue) {
        value = maskedValue;
        target.value = value;
      }
    }

    this._value = value;


    this.onChange(value);


    this.valueChange.emit(value);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.shouldApplyEmailValidation()) {
      const emailValue = (control.value || '').toString().trim();
      if (emailValue && !ValidationService.validateEmail(emailValue)) {
        return { email: true };
      }
    }

    if (this.data.type !== 'date') return null;

    const val = control.value;
    // Let standard Validators.required handle empty values
    if (!val || val === '' || (typeof val === 'string' && val.trim() === '')) return null;
    if (typeof val === 'object' && !(val instanceof Date)) return null;

    // If onDateChange emitted our fallback string, it's a bad date format
    const d = new Date(val);
    if (isNaN(d.getTime())) {
      return this.data.required ? { required: true } : null;
    }
    if (d.getFullYear() <= 1) {
      return this.data.required ? { required: true } : null;
    }

    // Pass min/max/filter errors up to the parent form
    if (this.minDate) {
      const minNormalized = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate());
      const valNormalized = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (valNormalized < minNormalized) return { minDate: true };
    }
    if (this.maxDate) {
      const maxNormalized = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate());
      const valNormalized = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (valNormalized > maxNormalized) return { maxDate: true };
    }
    if (this.dateFilter && !this.dateFilter(d)) {
      return { dateFilter: true };
    }

    return null;
  }

  onDateChange(event: MatDatepickerInputEvent<Date>): void {

    if (this.ngControl?.control?.hasError('invalidDate')) {
      return;
    }
    if (!event.value) {
      this._dateValue = null;
      this._value = '';
      this.onChange('');
      this.valueChange.emit('');
      return;
    }

    const date = event.value;
    this._dateValue = date;

    const formattedDate =
      String(date.getMonth() + 1).padStart(2, '0') + '/' +
      String(date.getDate()).padStart(2, '0') + '/' +
      date.getFullYear();

    this._value = formattedDate;
    this.onChange(formattedDate);
    this.valueChange.emit(formattedDate);
  }

  onBlur(): void {
    this.onTouched();
  }

  handleStartIconClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.data.startIcon?.onIconClick) this.data.startIcon.onIconClick();
  }

  handleEndIconClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.data.endIcon?.onIconClick) this.data.endIcon.onIconClick();
  }

  isTemplate(value: any): value is TemplateRef<any> {
    return value instanceof TemplateRef;
  }

  onDateKeyDown(event: KeyboardEvent): void {
  const input = event.target as HTMLInputElement;

  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    '/'
  ];

  if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
    event.preventDefault();
    return;
  }

  const value = input.value;

  // Prevent month > 12
  if (value.length === 1 && /^\d$/.test(event.key)) {
    const month = Number(value + event.key);

    if (month > 12) {
      event.preventDefault();
      return;
    }
  }

  // Prevent day > 31
  if (value.length === 4 && /^\d$/.test(event.key)) {
    const day = Number(value.substring(3) + event.key);

    if (day > 31) {
      event.preventDefault();
      return;
    }
  }
}

onDateInput(event: Event): void {
  const input = event.target as HTMLInputElement;

  let value = input.value.replace(/\D/g, '');
  value = value.substring(0, 8);


  if (value.length > 4) {
    value = `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4)}`;
  } else if (value.length > 2) {
    value = `${value.substring(0, 2)}/${value.substring(2)}`;
  }

  input.value = value;
  this._value = value; // ADDED: Keep internal value in sync while typing

if (value.length === 10) {
      const [month, day, year] = value.split('/').map(Number);
      const date = new Date(year, month - 1, day);

      const isValid =
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day;

      if (!isValid) {
        this.ngControl?.control?.setErrors({
          ...(this.ngControl?.control?.errors || {}),
          invalidDate: true
        });
        this.onChange(value); // ADDED: Notify parent form of invalid format
        return;
      }

      const errors = { ...(this.ngControl?.control?.errors || {}) };
      delete errors['invalidDate'];
      this.ngControl?.control?.setErrors(Object.keys(errors).length ? errors : null);

      this._dateValue = date; // ADD THIS: Sync the native Date object
      this._value = value;
      this.onChange(value);
    } else {
      // ADDED: Emits cleared value and resets errors on backspace
      if (value === '') {
        this._dateValue = null;
      }
      if (this.ngControl?.control?.hasError('invalidDate')) {
        const errors = { ...(this.ngControl?.control?.errors || {}) };
        delete errors['invalidDate'];
        this.ngControl?.control?.setErrors(Object.keys(errors).length ? errors : null);
      }
      this.onChange(value);
    }
}


  onDatePaste(event: ClipboardEvent): void {
    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const pastedText = event.clipboardData?.getData('text') || '';

    let digits = pastedText.replace(/\D/g, '').substring(0, 8);

    let formatted = '';

    if (digits.length >= 1) {
      formatted = digits.substring(0, Math.min(2, digits.length));
    }

    if (digits.length > 2) {
      formatted += '/' + digits.substring(2, Math.min(4, digits.length));
    }

    if (digits.length > 4) {
      formatted += '/' + digits.substring(4, 8);
    }

    input.value = formatted;
    this._value = formatted;
    this.onChange(this._value);
  }

  private validateDate(dateString: string): boolean {
  const parts = dateString.split('/');

  if (parts.length !== 3) {
    return false;
  }

  const month = Number(parts[0]);
  const day = Number(parts[1]);
  const year = Number(parts[2]);

  // Basic validation
  if (
    isNaN(month) ||
    isNaN(day) ||
    isNaN(year) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    year < 1000
  ) {
    return false;
  }

  // Actual calendar validation
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}
}
